import { CalendarDate, toZoned } from '@internationalized/date';
import {
  CurrencyCode,
  MoneyInput,
  PurchaseLoadUpdateInput,
  UpdatePurchaseLoadDeductionInput,
  UpdatePurchaseLoadLineItemInput,
  WeightDisplayUnit,
  WeightInput,
} from '@rematter/frontend-lib-api-client';

import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';

const calendarDateToZonedDateTime = (date: CalendarDate): string =>
  toZoned(date, 'UTC').toString();

export const purchaseLoadUpdateAtom = createMutationAtomFactory(
  'trade.purchaseLoadUpdate',
  (params: {
    id: string;
    receivedDate?: CalendarDate | null;
    facilityId?: string | null;
    currencyCode?: CurrencyCode | null;
    description?: string | null;
    purchaseOrderId?: string | null;
    serviceId?: string | null;
    supplierAccountId?: string | null;
    supplierAddressId?: string | null;
    containerTypeId?: string | null;
    carrierAccountId?: string | null;
    freightCost?: MoneyInput | null;
    freightTerms?: string | null;
    bolNumber?: string | null;
    heavyWeight?: WeightInput | null;
    lightWeight?: WeightInput | null;
    weightDisplayUnit?: WeightDisplayUnit | null;
    shippingCarrierName?: string | null;
    scac?: string | null;
    vehicleLicensePlate?: string | null;
    driverName?: string | null;
    truckNumber?: string | null;
    trailerNumber?: string | null;
    containerNumber?: string | null;
    sealNumber?: string | null;
    internalNotes?: string | null;
    externalNotes?: string | null;
    receivingTicketNumber?: string | null;
    receivedTicketIssueDate?: CalendarDate | null;
    lineItems?: UpdatePurchaseLoadLineItemInput[];
    deductions?: UpdatePurchaseLoadDeductionInput[];
    expectedVersion?: number;
    // For result only
    failedToSaveMessage?: (label: string) => string;
  }) => {
    const input: PurchaseLoadUpdateInput = {};
    if (params.expectedVersion !== undefined)
      input.expectedVersion = params.expectedVersion;

    if (params.receivedDate)
      input.receivedDate = calendarDateToZonedDateTime(params.receivedDate);
    if (params.facilityId) input.facilityId = params.facilityId;
    if (params.currencyCode !== undefined)
      input.currencyCode = params.currencyCode;
    if (params.description !== undefined)
      input.description = params.description;
    if (params.purchaseOrderId !== undefined)
      input.purchaseOrderId = params.purchaseOrderId;
    if (params.serviceId !== undefined) input.serviceId = params.serviceId;
    if (params.supplierAccountId !== undefined)
      input.supplierAccountId = params.supplierAccountId;
    if (params.supplierAddressId !== undefined)
      input.supplierAddressId = params.supplierAddressId;
    if (params.containerTypeId !== undefined)
      input.containerTypeId = params.containerTypeId;
    if (params.carrierAccountId !== undefined)
      input.carrierAccountId = params.carrierAccountId;
    if (params.freightCost !== undefined)
      input.freightCost = params.freightCost;
    if (params.freightTerms !== undefined)
      input.freightTerms = params.freightTerms;
    if (params.bolNumber !== undefined) input.bolNumber = params.bolNumber;
    if (params.heavyWeight !== undefined)
      input.heavyWeight = params.heavyWeight;
    if (params.lightWeight !== undefined)
      input.lightWeight = params.lightWeight;
    if (params.weightDisplayUnit !== undefined)
      input.weightDisplayUnit = params.weightDisplayUnit;
    if (params.shippingCarrierName !== undefined)
      input.shippingCarrierName = params.shippingCarrierName;
    if (params.scac !== undefined) input.scac = params.scac;
    if (params.vehicleLicensePlate !== undefined)
      input.vehicleLicensePlate = params.vehicleLicensePlate;
    if (params.driverName !== undefined) input.driverName = params.driverName;
    if (params.truckNumber !== undefined)
      input.truckNumber = params.truckNumber;
    if (params.trailerNumber !== undefined)
      input.trailerNumber = params.trailerNumber;
    if (params.containerNumber !== undefined)
      input.containerNumber = params.containerNumber;
    if (params.sealNumber !== undefined) input.sealNumber = params.sealNumber;
    if (params.internalNotes !== undefined)
      input.internalNotes = params.internalNotes;
    if (params.externalNotes !== undefined)
      input.externalNotes = params.externalNotes;
    if (params.receivingTicketNumber !== undefined)
      input.receivingTicketNumber = params.receivingTicketNumber;
    if (params.receivedTicketIssueDate)
      input.receivedTicketIssueDate = params.receivedTicketIssueDate.toString();
    if (params.lineItems !== undefined) input.lineItems = params.lineItems;
    if (params.deductions !== undefined) input.deductions = params.deductions;

    return {
      tradePurchaseLoadUpdate: {
        __args: {
          id: params.id,
          input,
        },
        id: true,
        version: true,
        lineItems: {
          id: true,
          type: true,
          itemId: true,
          tagId: true,
        },
        deductions: {
          id: true,
          deductionTypeId: true,
          applicationType: true,
          calculatedAmount: { amount: true, currencyCode: true },
          weightValue: { value: true, unit: true },
          priceValue: { amount: true, currencyCode: true },
          percentage: true,
          purchaseLoadLineItemId: true,
          notes: true,
        },
      },
    };
  },
  { showLoadingOverlay: false },
);
